// Cliente HTTP do builder — fala com o Worker NanoApp via cookie de sessão.
//
// Convenções:
//   - credentials: 'include' em tudo (Worker setá cookie nanoapp_session)
//   - Erros viram throw com `status` + `error` (texto)
//   - 401 dispara o evento global 'nanoapp:unauthorized' pra App.tsx limpar
//     a sessão e voltar pra AuthScreen
//
// Endpoints reusados do backend existente (Sprint 1.1+ já em produção):
//   /auth/{me,login,signup,logout,forgot-password,reset-password}
//   /forms (GET list, POST create, GET :id, PATCH :id, DELETE :id, POST :id/duplicate)
//   /forms/:id/responses (GET list, DELETE :rid)
//   /folders (GET, POST, PATCH :id, DELETE :id)

export const API_URL: string =
  (import.meta.env.VITE_API_URL as string | undefined)?.replace(/\/$/, '') ||
  'https://nanoapp-api.convergente.app.br';

export class ApiError extends Error {
  status: number;
  body: unknown;
  constructor(status: number, message: string, body?: unknown) {
    super(message);
    this.status = status;
    this.body = body;
  }
}

async function request<T>(
  path: string,
  opts: { method?: string; body?: unknown; headers?: Record<string, string> } = {},
): Promise<T> {
  const headers: Record<string, string> = { ...(opts.headers ?? {}) };
  let body: BodyInit | undefined;
  if (opts.body !== undefined) {
    headers['Content-Type'] = 'application/json';
    body = JSON.stringify(opts.body);
  }
  const res = await fetch(API_URL + path, {
    method: opts.method ?? 'GET',
    credentials: 'include',
    headers,
    body,
  });
  if (res.status === 401) {
    // Sinaliza pra App.tsx limpar sessão (sem await pra não atrasar throw).
    window.dispatchEvent(new CustomEvent('nanoapp:unauthorized'));
  }
  const text = await res.text();
  let parsed: unknown = null;
  if (text) {
    try { parsed = JSON.parse(text); } catch { parsed = text; }
  }
  if (!res.ok) {
    const msg =
      (parsed && typeof parsed === 'object' && 'error' in parsed && typeof (parsed as { error: unknown }).error === 'string')
        ? String((parsed as { error: string }).error)
        : `HTTP ${res.status}`;
    throw new ApiError(res.status, msg, parsed);
  }
  return parsed as T;
}

// ─── Tipos da API (DTOs do worker) ────────────────────────────────

export interface UserDTO {
  id: string;
  email: string;
  name?: string | null;
  isPlatformAdmin?: boolean;
  emailVerified?: boolean;
  /** Preferências server-side (Sprint 8). Pode estar vazio em users antigos. */
  settings?: Record<string, unknown>;
}

export interface FormDTO {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  schema: import('@nanoapp/types').FormField[];   // backend já fez safeParse
  settings: import('@nanoapp/types').FormSettings;
  theme: import('@nanoapp/types').FormTheme;
  app_id: string | null;
  /** 2026-05-18: tag visual de cliente (recurso avulso). NULL = global. */
  customer_id?: string | null;
  target_collection_id: string | null;
  target_collection_op: string | null;
  submit_policy: 'public' | 'token' | 'authenticated';
  folder_id: string | null;
  created_at: string;
  updated_at: string;
  /** Adicionado pela GET /forms (lista) — backend agrega via JOIN. */
  responses_count?: number;
}

export interface FolderDTO {
  id: string;
  title: string;
  description: string | null;
  color: string;
  active: boolean;
  created_at: string;
  updated_at: string;
}

export interface ResponseDTO {
  id: string;
  form_id: string;
  data: unknown;
  ip: string | null;
  user_agent: string | null;
  created_at: string;
  meta?: unknown;
}

export interface PaginatedResponse<T> {
  items: T[];
  pagination: { total: number; limit: number; offset: number; hasMore: boolean };
}

// ─── auth ─────────────────────────────────────────────────────────

export const authApi = {
  me: () =>
    request<{ user: UserDTO } | null>('/auth/me').catch((e) => {
      if (e instanceof ApiError && e.status === 401) return null;
      throw e;
    }),
  login: (email: string, password: string) =>
    request<{ user: UserDTO }>('/auth/login', { method: 'POST', body: { email, password } }),
  signup: (email: string, password: string) =>
    request<{ user: UserDTO }>('/auth/signup', { method: 'POST', body: { email, password } }),
  logout: () => request<{ ok: true }>('/auth/logout', { method: 'POST' }),
  forgotPassword: (email: string) =>
    request<{ ok: true }>('/auth/forgot-password', { method: 'POST', body: { email } }),
  resetPassword: (token: string, password: string) =>
    request<{ ok: true }>('/auth/reset-password', { method: 'POST', body: { token, password } }),
  getSettings: () =>
    request<{ settings: Record<string, unknown> }>('/auth/me/settings'),
  saveSettings: (settings: Record<string, unknown>) =>
    request<{ settings: Record<string, unknown> }>('/auth/me/settings', { method: 'PUT', body: { settings } }),
};

// ─── forms ────────────────────────────────────────────────────────

export const formsApi = {
  list: (params?: { folder_id?: string | null; search?: string; limit?: number; offset?: number }) => {
    const q = new URLSearchParams();
    if (params?.search) q.set('search', params.search);
    if (params?.folder_id !== undefined && params.folder_id !== null) q.set('folder_id', params.folder_id);
    if (params?.limit !== undefined) q.set('limit', String(params.limit));
    if (params?.offset !== undefined) q.set('offset', String(params.offset));
    const qs = q.toString();
    return request<PaginatedResponse<FormDTO>>('/forms' + (qs ? '?' + qs : ''));
  },
  get: (id: string) => request<{ form: FormDTO }>(`/forms/${id}`),
  create: (input: { name: string; slug?: string; description?: string; schema?: unknown; settings?: unknown; theme?: unknown; folder_id?: string | null; app_id?: string | null; target_collection_id?: string | null; target_collection_op?: 'insert' | 'update' | 'upsert' | 'delete'; submit_policy?: 'public' | 'token' | 'authenticated' }) =>
    request<{ form: FormDTO }>('/forms', { method: 'POST', body: input }),
  update: (id: string, patch: Partial<{ name: string; slug: string; description: string; schema: unknown; settings: unknown; theme: unknown; folder_id: string | null; app_id: string | null; target_collection_id: string | null; target_collection_op: 'insert' | 'update' | 'upsert' | 'delete'; submit_policy: 'public' | 'token' | 'authenticated'; customer_id: string | null }>) =>
    request<{ form: FormDTO }>(`/forms/${id}`, { method: 'PATCH', body: patch }),
  remove: (id: string) =>
    request<{ ok: true }>(`/forms/${id}`, { method: 'DELETE' }),
  // duplicate: client-side via GET + POST (backend não tem endpoint dedicado).


  responses: (id: string, params?: { limit?: number; offset?: number; search?: string }) => {
    const q = new URLSearchParams();
    if (params?.limit !== undefined) q.set('limit', String(params.limit));
    if (params?.offset !== undefined) q.set('offset', String(params.offset));
    if (params?.search) q.set('search', params.search);
    const qs = q.toString();
    return request<PaginatedResponse<ResponseDTO>>(`/forms/${id}/responses` + (qs ? '?' + qs : ''));
  },
  removeResponse: (formId: string, responseId: string) =>
    request<{ ok: true }>(`/forms/${formId}/responses/${responseId}`, { method: 'DELETE' }),
};

// ─── folders ──────────────────────────────────────────────────────

export const foldersApi = {
  list: () => request<PaginatedResponse<FolderDTO>>('/folders'),
  create: (input: { title: string; description?: string; color?: string; active?: boolean }) =>
    request<{ folder: FolderDTO }>('/folders', { method: 'POST', body: input }),
  update: (id: string, patch: Partial<{ title: string; description: string; color: string; active: boolean }>) =>
    request<{ folder: FolderDTO }>(`/folders/${id}`, { method: 'PATCH', body: patch }),
  remove: (id: string) =>
    request<{ ok: true }>(`/folders/${id}`, { method: 'DELETE' }),
};

// ─── apps ─────────────────────────────────────────────────────────

export interface AppDTO {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  config: unknown;
  /** 2026-05-18: FK opcional pra customers. NULL = "Workspace pessoal". */
  customer_id?: string | null;
  created_at: string;
  updated_at: string;
}

// 2026-05-18: Customer DTO + API client (Workspace de Clientes)
export interface CustomerDTO {
  id: string;
  owner_id: string;
  name: string;
  slug: string;
  icon?: string | null;
  color?: string | null;
  contact_email?: string | null;
  contact_phone?: string | null;
  notes?: string | null;
  created_at: string;
  updated_at: string;
}

export interface CustomerWithStats extends CustomerDTO {
  appsCount: number;
  /** 2026-05-18: agora agregado de verdade no list endpoint (era hardcoded 0). */
  formsCount: number;
  /** 2026-05-18: agora agregado de verdade no list endpoint (per-app + avulsas via customer_id). */
  collectionsCount: number;
  recordsToday: number;
  lastActivity?: string | null;
}

export interface CustomerDashboard {
  customer: CustomerDTO;
  appsCount: number;
  formsCount: number;
  /** 2026-05-18: count agregado de collections (per-app + tagueadas direto via customer_id). */
  collectionsCount?: number;
  recordsToday: number;
  triggersCount: number;
  lastActivity?: string | null;
}

// 2026-05-18 Phase 5: Activity feed
export interface ActivityItem {
  type: 'record_created' | 'record_updated' | 'form_response' | 'app_updated' | 'trigger_ok' | 'trigger_error';
  app_id: string | null;
  customer_id: string | null;
  message: string;
  timestamp: string;
  ref_id: string;
  ref_kind: 'record' | 'form_response' | 'app' | 'trigger_run';
  ref_label?: string;
}

export const activityApi = {
  /** Lista atividade recente do owner. customer_id opcional filtra. */
  list: (params?: { customer_id?: string; limit?: number }) => {
    const qs: string[] = [];
    if (params?.customer_id) qs.push(`customer_id=${encodeURIComponent(params.customer_id)}`);
    if (params?.limit) qs.push(`limit=${params.limit}`);
    const query = qs.length ? '?' + qs.join('&') : '';
    return request<{ items: ActivityItem[]; since: string }>(`/activity${query}`);
  },
};

export const customersApi = {
  list: () => request<{ items: CustomerWithStats[] }>('/customers'),
  get: (id: string) => request<{ customer: CustomerDTO }>(`/customers/${id}`),
  getBySlug: (slug: string) =>
    request<{ customer: CustomerDTO }>(`/customers/by-slug/${slug}`),
  create: (input: Partial<Pick<CustomerDTO, 'name' | 'slug' | 'icon' | 'color' | 'contact_email' | 'contact_phone' | 'notes'>>) =>
    request<{ customer: CustomerDTO }>('/customers', { method: 'POST', body: input }),
  update: (id: string, patch: Partial<Pick<CustomerDTO, 'name' | 'slug' | 'icon' | 'color' | 'contact_email' | 'contact_phone' | 'notes'>>) =>
    request<{ customer: CustomerDTO }>(`/customers/${id}`, { method: 'PATCH', body: patch }),
  remove: (id: string) =>
    request<{ ok: true; appsUnlinked: number }>(`/customers/${id}`, { method: 'DELETE' }),
  dashboard: (id: string) =>
    request<CustomerDashboard>(`/customers/${id}/dashboard`),
  checkSlug: (slug: string) =>
    request<{ available: boolean }>(`/customers/check-slug/${encodeURIComponent(slug)}`),
};

export const appsApi = {
  list: (params?: { customer_id?: string | null }) => {
    const q: Record<string, string> = {};
    if (params?.customer_id === null) q.customer_id = 'null';
    else if (params?.customer_id) q.customer_id = params.customer_id;
    const qs = Object.keys(q).length > 0 ? '?' + new URLSearchParams(q).toString() : '';
    return request<PaginatedResponse<AppDTO>>(`/apps${qs}`);
  },
  get: (id: string) => request<{ app: AppDTO }>(`/apps/${id}`),
  getConfig: (id: string) => request<{ app: AppDTO; collections?: unknown[]; pages?: unknown[] }>(`/apps/${id}/config`),
  create: (input: { name: string; slug?: string; description?: string; config?: unknown; customer_id?: string | null }) =>
    request<{ app: AppDTO }>('/apps', { method: 'POST', body: input }),
  update: (id: string, patch: Partial<{ name: string; slug: string; description: string; config: unknown; customer_id: string | null }>) =>
    request<{ app: AppDTO }>(`/apps/${id}`, { method: 'PATCH', body: patch }),
  remove: (id: string) => request<{ ok: true }>(`/apps/${id}`, { method: 'DELETE' }),
  previewToken: (id: string) =>
    request<{ token: string; expires_at: string; app_id: string }>(`/apps/${id}/preview-token`, { method: 'POST' }),
  /**
   * 2026-05-13: token de longa duração (default 30 dias, max 365) pra embed
   * no HTML exportado. Plus seguro que previewToken (1h) que expirava rápido.
   *
   * Por default, **auto-revoga** export-tokens antigos do mesmo app — só fica
   * 1 token ativo por vez. HTMLs antigos invalidam, mas não acumulam órfãos.
   * Opt-out: passar { keep_previous: true } pra preservar tokens antigos.
   */
  exportToken: (id: string, opts?: { ttl_days?: number; keep_previous?: boolean }) =>
    request<{ token: string; expires_at: string; ttl_days: number; revoked_previous: number }>(
      `/apps/${id}/export-token`,
      { method: 'POST', body: opts ?? {} },
    ),
  // Sprint 22.3: gestão de app_users (login/senha do app standalone)
  listUsers: (id: string) => request<PaginatedResponse<AppUserDTO>>(`/apps/${id}/users`),
  createUser: (id: string, input: { email: string; password: string; display_name?: string }) =>
    request<{ user: AppUserDTO }>(`/apps/${id}/users`, { method: 'POST', body: input }),
  updateUser: (id: string, userId: string, patch: { password?: string; display_name?: string }) =>
    request<{ user: AppUserDTO }>(`/apps/${id}/users/${userId}`, { method: 'PATCH', body: patch }),
  removeUser: (id: string, userId: string) =>
    request<{ ok: true }>(`/apps/${id}/users/${userId}`, { method: 'DELETE' }),
  // 1C.5/1C.4: export, import, clone
  exportJson: (id: string) =>
    request<unknown>(`/apps/${id}/export`),
  importJson: (input: { pkg: unknown; name?: string; slug?: string }) =>
    request<{ app: AppDTO; collectionsCreated: number }>('/apps/import', { method: 'POST', body: input }),
  /**
   * 2026-05-18 (deep clone): duplica app + collections + forms + triggers + pages
   * com remapping de TODAS as referências cruzadas (collectionId, formId, triggerId,
   * pageId, sidePanelDetailPageId).
   *
   * NÃO copia: records, form_responses, trigger_runs, app_users, tokens, publishes,
   * media (cada um por motivo claro — security + 1:1 com original + isolamento).
   *
   * Response retorna stats com counts de cada entidade duplicada.
   */
  clone: (id: string, input: { name?: string; slug?: string; customer_id?: string | null } = {}) =>
    request<{
      app: AppDTO;
      stats: {
        collections: number;
        forms: number;
        triggers: number;
        pages: number;
      };
    }>(`/apps/${id}/clone`, { method: 'POST', body: input }),
};

export interface AppUserDTO {
  id: string;
  app_id: string;
  email: string;
  display_name: string | null;
  email_verified: boolean;
  created_at: string;
  updated_at: string;
}

// ─── collections ──────────────────────────────────────────────────

export interface CollectionDTO {
  id: string;
  app_id: string | null;
  name: string;
  label: string;
  icon: string | null;
  fields: import('@nanoapp/types').CollectionField[];
  permissions: import('@nanoapp/types').CollectionPermissions;
  /** Sprint 19: 'uuid' (default) ou 'sequential'. */
  id_strategy?: 'uuid' | 'sequential';
  /** 2026-05-13: CollectionConfig (composite, etc). Migration 0018. */
  config?: import('@nanoapp/types').CollectionConfig;
  /** 2026-05-18: tag visual de cliente (recurso avulso). NULL = global.
   *  Não trava permissões — apenas organização visual em /customers/:slug. */
  customer_id?: string | null;
  created_at: string;
  updated_at: string;
}

export const collectionsApi = {
  /** Lista todas coleções do owner (incluindo shared, app_id=null). */
  list: () => request<PaginatedResponse<CollectionDTO>>('/collections'),
  /** Lista coleções de um app (vinculadas via app_id). */
  listByApp: (appId: string) => request<PaginatedResponse<CollectionDTO>>(`/apps/${appId}/collections`),
  get: (id: string) => request<{ collection: CollectionDTO }>(`/collections/${id}`),
  create: (input: { app_id?: string | null; name: string; label?: string; icon?: string; fields?: unknown; permissions?: unknown; id_strategy?: 'uuid' | 'sequential'; config?: import('@nanoapp/types').CollectionConfig; customer_id?: string | null }) =>
    request<{ collection: CollectionDTO }>('/collections', { method: 'POST', body: input }),
  createInApp: (appId: string, input: { name: string; label?: string; icon?: string; fields?: unknown; permissions?: unknown; id_strategy?: 'uuid' | 'sequential'; config?: import('@nanoapp/types').CollectionConfig }) =>
    request<{ collection: CollectionDTO }>(`/apps/${appId}/collections`, { method: 'POST', body: input }),
  update: (id: string, patch: Partial<{ name: string; label: string; icon: string; fields: unknown; permissions: unknown; app_id: string | null; id_strategy: 'uuid' | 'sequential'; config: import('@nanoapp/types').CollectionConfig; customer_id: string | null }>) =>
    request<{ collection: CollectionDTO }>(`/collections/${id}`, { method: 'PATCH', body: patch }),
  remove: (id: string) => request<{ ok: true }>(`/collections/${id}`, { method: 'DELETE' }),
  /** Soft-deleta TODOS records ativos. Próximo seq volta a #1. Owner-only. */
  resetRecords: (id: string) =>
    request<{ ok: true; affected: number }>(`/collections/${id}/reset-records`, {
      method: 'POST',
      body: { confirm: true },
    }),
  /** Lista coleções soft-deleted do owner (até 50 mais recentes). */
  trash: () => request<PaginatedResponse<CollectionDTO & { deleted_at: string }>>('/collections/trash'),
  /** Restaura coleção (limpa deleted_at). */
  restore: (id: string) =>
    request<{ ok: true; restored: { id: string; name: string } }>(`/collections/${id}/restore`, {
      method: 'POST',
    }),
  /** Restaura records soft-deleted da coleção. ?since=ISO filtra por data. */
  restoreRecords: (id: string, opts?: { since?: string }) =>
    request<{ ok: true; affected: number }>(
      `/collections/${id}/restore-records${opts?.since ? `?since=${encodeURIComponent(opts.since)}` : ''}`,
      { method: 'POST' },
    ),
};

// ─── records (CRUD via cookie owner) ──────────────────────────────

export interface RecordDTO {
  id: string;
  collection_id: string;
  data: unknown;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
  created_by_token: string | null;
  created_by_role: string | null;
  /** Sprint 19: número user-facing quando id_strategy='sequential'. */
  seq?: number | null;
}

export interface RecordsListResult {
  items: RecordDTO[];
  pagination: { total: number; limit: number; offset: number; hasMore: boolean };
  serverTime?: string;
}

export const recordsApi = {
  list: (collectionId: string, params?: { limit?: number; offset?: number; search?: string; since?: string }) => {
    const q = new URLSearchParams();
    if (params?.limit !== undefined) q.set('limit', String(params.limit));
    if (params?.offset !== undefined) q.set('offset', String(params.offset));
    if (params?.search) q.set('search', params.search);
    if (params?.since) q.set('since', params.since);
    const qs = q.toString();
    return request<RecordsListResult>(`/collections/${collectionId}/records` + (qs ? '?' + qs : ''));
  },
  get: (id: string) => request<{ record: RecordDTO }>(`/records/${id}`),
  create: (collectionId: string, data: Record<string, unknown>) =>
    request<{ record: RecordDTO }>(`/collections/${collectionId}/records`, { method: 'POST', body: { data } }),
  update: (id: string, data: Record<string, unknown>, merge: boolean = true) =>
    request<{ record: RecordDTO }>(`/records/${id}`, { method: 'PATCH', body: { data, merge } }),
  remove: (id: string) => request<{ ok: true }>(`/records/${id}`, { method: 'DELETE' }),
};

// ─── integrations ─────────────────────────────────────────────────

export interface IntegrationDTO {
  id: string;
  owner_id: string;
  kind: string;            // 'brevo' | 'telegram' | 'discord' | 'webhook' | etc.
  name: string;
  enabled: boolean;
  configKeys: string[];    // hint dos nomes (segredos ficam server-side)
  last_test_at: string | null;
  last_test_ok: boolean;
  last_test_error: string | null;
  created_at: string;
  updated_at: string;
}

/** Backwards compat — código antigo importava IntegrationListItem. */
export type IntegrationListItem = IntegrationDTO;

export const integrationsApi = {
  list: () => request<PaginatedResponse<IntegrationDTO>>('/integrations'),
  /** Aceita tanto a forma legacy (kind, name, config) quanto objeto único. */
  create: (kindOrInput: string | { kind: string; name: string; config: Record<string, unknown>; enabled?: boolean }, name?: string, config?: Record<string, unknown>) => {
    const body = typeof kindOrInput === 'string'
      ? { kind: kindOrInput, name: name!, config: config ?? {} }
      : kindOrInput;
    return request<{ integration: IntegrationDTO }>('/integrations', { method: 'POST', body });
  },
  update: (id: string, patch: Partial<{ name: string; config: Record<string, unknown>; enabled: boolean }>) =>
    request<{ integration: IntegrationDTO }>(`/integrations/${id}`, { method: 'PATCH', body: patch }),
  remove: (id: string) => request<{ ok: true }>(`/integrations/${id}`, { method: 'DELETE' }),
  test: (id: string, opts?: { to?: string }) =>
    request<{ ok: boolean; output?: unknown; error?: string }>(`/integrations/${id}/test`, { method: 'POST', body: opts ?? {} }),
};

// ─── app_tokens ───────────────────────────────────────────────────

export interface AppTokenDTO {
  id: string;
  app_id: string | null;
  form_id: string | null;
  name: string;
  role: string;
  permissions: unknown;
  created_at: string;
  expires_at: string | null;
  revoked_at: string | null;
}

export interface AppTokenWithValue extends AppTokenDTO {
  /** Token assinado HMAC. Só aparece na criação — backend não armazena. */
  value: string;
}

export const appTokensApi = {
  listByApp: (appId: string) => request<PaginatedResponse<AppTokenDTO>>(`/apps/${appId}/tokens`),
  create: (appId: string, input: { name: string; role: string; permissions?: unknown; expires_at?: string }) =>
    request<{ token: AppTokenWithValue }>(`/apps/${appId}/tokens`, { method: 'POST', body: input }),
  revoke: (id: string) => request<{ ok: true }>(`/app_tokens/${id}`, { method: 'DELETE' }),
};

// ─── triggers ─────────────────────────────────────────────────────

export type TriggerEventName = 'form_submit' | 'record_create' | 'record_update' | 'record_delete' | 'schedule' | 'manual';

export interface TriggerSpec {
  event?: TriggerEventName;
  formPageId?: string;
  collectionId?: string;
  cron?: string;
}

/**
 * Backend serialize() flatten: o JSON `definition` é decomposto em
 * trigger/actions/condition/errorPolicy diretamente no DTO. Para
 * create/update aceitamos a forma plana e o backend recompõe.
 */
export interface TriggerDTO {
  id: string;
  app_id: string;
  name: string;
  enabled: boolean;
  trigger?: TriggerSpec;
  condition?: unknown;
  actions: Array<Record<string, unknown>>;
  errorPolicy?: unknown;
  inputs?: Array<Record<string, unknown>>;
  created_at: string;
  updated_at: string;
}

export interface TriggerInputBody {
  name: string;
  enabled?: boolean;
  trigger?: TriggerSpec;
  condition?: unknown;
  actions?: Array<Record<string, unknown>>;
  errorPolicy?: unknown;
  inputs?: Array<Record<string, unknown>>;
  /** Alternativa: passar definition wrapper completo. */
  definition?: { trigger?: TriggerSpec; actions?: Array<Record<string, unknown>>; condition?: unknown; errorPolicy?: unknown; inputs?: Array<Record<string, unknown>> };
}

export interface TriggerRunDTO {
  id: string;
  trigger_id: string;
  status: 'success' | 'error';
  input: unknown;
  output: unknown;
  started_at: string;
  finished_at: string;
  attempt: number;
}

export const triggersApi = {
  listByApp: (appId: string) => request<PaginatedResponse<TriggerDTO>>(`/apps/${appId}/triggers`),
  get: (id: string) => request<{ trigger: TriggerDTO }>(`/triggers/${id}`),
  create: (appId: string, input: TriggerInputBody) =>
    request<{ trigger: TriggerDTO }>(`/apps/${appId}/triggers`, { method: 'POST', body: input }),
  update: (id: string, patch: Partial<TriggerInputBody>) =>
    request<{ trigger: TriggerDTO }>(`/triggers/${id}`, { method: 'PATCH', body: patch }),
  remove: (id: string) => request<{ ok: true }>(`/triggers/${id}`, { method: 'DELETE' }),
  runs: (id: string, params?: { limit?: number; offset?: number }) => {
    const q = new URLSearchParams();
    if (params?.limit !== undefined) q.set('limit', String(params.limit));
    if (params?.offset !== undefined) q.set('offset', String(params.offset));
    const qs = q.toString();
    return request<PaginatedResponse<TriggerRunDTO>>(`/triggers/${id}/runs` + (qs ? '?' + qs : ''));
  },
  /** Lista runs de TODAS as triggers do app, com filtros opcionais. */
  appRuns: (appId: string, params?: { limit?: number; offset?: number; triggerId?: string; status?: 'success' | 'error' }) => {
    const q = new URLSearchParams();
    if (params?.limit !== undefined) q.set('limit', String(params.limit));
    if (params?.offset !== undefined) q.set('offset', String(params.offset));
    if (params?.triggerId) q.set('trigger_id', params.triggerId);
    if (params?.status) q.set('status', params.status);
    const qs = q.toString();
    return request<PaginatedResponse<{
      id: string; trigger_id: string; trigger_name: string; status: string;
      input: unknown; output: unknown;
      started_at: string; finished_at: string | null; attempt: number;
    }>>(`/apps/${appId}/trigger-runs` + (qs ? '?' + qs : ''));
  },
  /** Roda uma trigger sob demanda (testar/manual). */
  runOnce: (id: string, body: { recordIds?: string[]; recordId?: string; inputs?: Record<string, unknown> } = {}) =>
    request<{ ok: boolean; runs: number; errors: string[] }>(`/triggers/${id}/run`, { method: 'POST', body }),
  /** Testa fetch externo pra ajudar mapeamento — só client-side, não persiste nada. */
  previewFetch: (input: {
    url: string;
    method?: 'GET' | 'POST';
    headers?: Record<string, string>;
    auth?: { type?: string; token?: string; user?: string; pass?: string; header?: string; value?: string };
    body?: string;
    dataPath?: string;
  }) => request<{
    ok: boolean;
    error?: string;
    count?: number;
    sample?: unknown;
    paths?: Array<{ path: string; type: string; sample: string }>;
    preview?: unknown;
  }>('/triggers/preview-fetch', { method: 'POST', body: input }),
};

// ─── publishes (2026-05-15 — 1-click publish pra URL pública) ─────

export interface PublishDTO {
  slug: string;
  kind: 'app' | 'form';
  targetId: string;
  publishedAt: number;
  sizeBytes: number;
  url: string;
}

export const publishesApi = {
  /** Publica HTML de um app. Server faz UPDATE se já existir; INSERT senão. */
  publishApp: (appId: string, input: { slug?: string; html: string }) =>
    request<{ publish: PublishDTO }>(`/apps/${appId}/publish`, { method: 'POST', body: input }),
  /** Publica HTML de um form individual. */
  publishForm: (formId: string, input: { slug?: string; html: string }) =>
    request<{ publish: PublishDTO }>(`/forms/${formId}/publish`, { method: 'POST', body: input }),
  /** Despublica (remove R2 + row). */
  unpublish: (slug: string) =>
    request<{ ok: true }>(`/publishes/${slug}`, { method: 'DELETE' }),
  /** Lista publishes do owner. */
  mine: () => request<{ items: PublishDTO[] }>('/publishes/mine'),
  /** Resolve publish atual de um app/form. publish:null se não publicado. */
  byTarget: (kind: 'app' | 'form', id: string) =>
    request<{ publish: PublishDTO | null }>(`/publishes/by-target?kind=${kind}&id=${id}`),
  /** Live validation do slug. available:true se livre OR mine:true se já é do owner. */
  checkSlug: (slug: string): Promise<{ ok: boolean; available: boolean; mine?: boolean; reason?: string }> =>
    request<{ ok: boolean; available: boolean; mine?: boolean; reason?: string }>(
      `/publishes/check-slug/${encodeURIComponent(slug)}`,
    ).catch((e: unknown) => {
      // 400/409 são "respostas semânticas", não exceções; preservamos shape.
      if (e instanceof ApiError && (e.status === 400 || e.status === 409)) {
        return { ok: false, available: false, reason: e.message };
      }
      throw e;
    }),
};
